import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import type { ElasticConfig } from '@/api/types';
import { REDACTION_SENTINEL } from '@/api/types';
import { Field, FormShell, SecretInput } from './shared';

interface Props {
  initial: ElasticConfig | null;
  onSubmit: (config: Partial<ElasticConfig>) => void;
  submitting: boolean;
  onCancel: () => void;
  onReset?: () => void;
}

export function ElasticForm({ initial, onSubmit, submitting, onCancel, onReset }: Props): JSX.Element {
  const { t } = useTranslation();
  const [url, setUrl] = React.useState(initial?.url ?? '');
  const [authMode, setAuthMode] = React.useState<'basic' | 'apikey'>(initial?.apiKey ? 'apikey' : 'basic');
  const [username, setUsername] = React.useState(initial?.username ?? '');
  const [password, setPassword] = React.useState(initial?.password ?? '');
  const [apiKey, setApiKey] = React.useState(initial?.apiKey ?? '');
  const [defaultLogsIndex, setDefaultLogsIndex] = React.useState(initial?.defaultLogsIndex ?? 'ugo-logs-*');

  return (
    <form
      id="integration-form"
      onSubmit={(e) => {
        e.preventDefault();
        const base = { url: url.trim(), defaultLogsIndex: defaultLogsIndex.trim() };
        if (authMode === 'apikey') {
          onSubmit({
            ...base,
            apiKey: apiKey === REDACTION_SENTINEL || !apiKey ? REDACTION_SENTINEL : apiKey,
            username: undefined,
            password: undefined,
          });
        } else {
          onSubmit({
            ...base,
            username: username.trim() || undefined,
            password: password === REDACTION_SENTINEL || !password ? REDACTION_SENTINEL : password,
            apiKey: undefined,
          });
        }
      }}
    >
      <FormShell submitting={submitting} onCancel={onCancel} onReset={onReset}>
        <Field label={t('integrations.form.elastic.url')} htmlFor="el-url" required hint={t('integrations.form.elastic.urlHint')}>
          <Input
            id="el-url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="http://elasticsearch:9200"
            required
          />
        </Field>
        <Field label={t('integrations.form.elastic.authMode')} htmlFor="el-auth-mode">
          <select
            id="el-auth-mode"
            value={authMode}
            onChange={(e) => setAuthMode(e.target.value as 'basic' | 'apikey')}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="basic">{t('integrations.form.elastic.authBasic')}</option>
            <option value="apikey">{t('integrations.form.elastic.authApiKey')}</option>
          </select>
        </Field>
        {authMode === 'basic' ? (
          <>
            <Field label={t('integrations.form.elastic.username')} htmlFor="el-user" hint={t('integrations.form.elastic.usernameHint')}>
              <Input id="el-user" value={username} onChange={(e) => setUsername(e.target.value)} />
            </Field>
            <Field label={t('integrations.form.elastic.password')} htmlFor="el-pwd" hint={t('integrations.form.elastic.passwordHint')}>
              <SecretInput id="el-pwd" value={password} onChange={setPassword} />
            </Field>
          </>
        ) : (
          <Field label={t('integrations.form.elastic.apiKeyLabel')} htmlFor="el-apikey" hint={t('integrations.form.elastic.apiKeyHint')}>
            <SecretInput id="el-apikey" value={apiKey} onChange={setApiKey} />
          </Field>
        )}
        <Field label={t('integrations.form.elastic.defaultIndex')} htmlFor="el-idx" required>
          <Input
            id="el-idx"
            value={defaultLogsIndex}
            onChange={(e) => setDefaultLogsIndex(e.target.value)}
            required
          />
        </Field>
      </FormShell>
    </form>
  );
}
